<script lang="ts">
import type { ThemeSlots } from '#layers/ui/types/tv';
import { tv } from 'tailwind-variants';

const emptyClasses = tv({
	slots: {
		root: 'flex flex-col items-center gap-6 p-6',
		icon: 'flex size-14 shrink-0 items-center justify-center rounded-lg border border-default text-muted',
		title: 'text-center text-lg font-semibold text-balance text-default',
		description: 'max-w-md text-center text-sm text-balance text-muted',
		action: '',
	},
	variants: {
		fill: {
			// Fill the region and optically fine-tune the vertical position with padding
			true: { root: 'h-full grow justify-center-safe overflow-y-auto pbe-17' },
			false: { root: 'justify-center' },
		},
	},
});
</script>

<script lang="ts" setup>
import { computed } from 'vue';

const { actionButtonIcon = 'lucide:plus', actionButtonPrimary = false, fill = false } = defineProps<{
	icon?: string;
	title?: string;
	description?: string;
	actionButtonLabel?: string;
	actionButtonIcon?: string;
	actionButtonPrimary?: boolean;
	fill?: boolean;
	ui?: ThemeSlots<typeof emptyClasses>;
}>();

defineEmits<{
	action: [];
}>();

const theme = computed(() => emptyClasses({ fill }));
</script>

<template>
	<div :class="theme.root({ class: ui?.root })">
		<div
			v-if="icon || $slots.illustration"
			:class="theme.icon({ class: ui?.icon })"
		>
			<slot name="illustration">
				<DIcon
					:name="icon"
					class="size-6"
				/>
			</slot>
		</div>

		<div
			v-if="title || description"
			class="flex flex-col items-center justify-between gap-1"
		>
			<div
				v-if="title"
				:class="theme.title({ class: ui?.title })"
			>
				{{ title }}
			</div>
			<div
				v-if="description"
				:class="theme.description({ class: ui?.description })"
			>
				{{ description }}
			</div>
		</div>

		<slot name="action" />

		<div
			v-if="actionButtonLabel && !$slots.action"
			:class="theme.action({ class: ui?.action })"
		>
			<DButton
				:icon="actionButtonIcon"
				:variant="actionButtonPrimary ? undefined : 'outline'"
				:color="actionButtonPrimary ? undefined : 'neutral'"
				size="sm"
				:label="actionButtonLabel"
				data-testid="empty-state-action"
				@click="$emit('action')"
			/>
		</div>
	</div>
</template>
